// src/components/settings/ShiftSettingsSection.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Snackbar,
  Alert,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import Section from "./Section";

type Props = {
  api: string; // e.g. "http://localhost:8000" or ""
  token?: string; // optional bearer token
};

type Shift = {
  id?: number; // server uses numeric ids
  name: string;
  start: string; // "HH:MM" 24h
  end: string; // "HH:MM" 24h
  days: number[]; // 0..6 (Sun..Sat)
};

type ShiftPayload = { shifts: Shift[] };

const DOW = [
  { label: "Sun", idx: 0 },
  { label: "Mon", idx: 1 },
  { label: "Tue", idx: 2 },
  { label: "Wed", idx: 3 },
  { label: "Thu", idx: 4 },
  { label: "Fri", idx: 5 },
  { label: "Sat", idx: 6 },
];

const emptyShift = (): Shift => ({
  name: "",
  start: "08:00",
  end: "17:00",
  days: [1, 2, 3, 4, 5],
});

function timeToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

function overlaps(a: Shift, b: Shift): boolean {
  const shared = a.days.some((d) => b.days.includes(d));
  if (!shared) return false;
  const aS = timeToMinutes(a.start);
  const aE = timeToMinutes(a.end);
  const bS = timeToMinutes(b.start);
  const bE = timeToMinutes(b.end);
  return aS < bE && bS < aE; // [start, end)
}

const ShiftSettingsSection: React.FC<Props> = ({ api, token }) => {
  const [loading, setLoading] = useState(true);
  const [orig, setOrig] = useState<ShiftPayload>({ shifts: [] }); // server state (with ids)
  const [value, setValue] = useState<ShiftPayload>({ shifts: [] }); // editable state (keeps ids when present)
  const [snack, setSnack] = useState<{
    open: boolean;
    msg: string;
    severity: "success" | "error" | "warning";
  }>({
    open: false,
    msg: "",
    severity: "success",
  });

  const endpoint = (path: string) => `${api}${path}`; // empty api -> relative
  const authHeader = token ? { Authorization: `Bearer ${token}` } : undefined;

  // ---- LOAD (GET /shifts) ----
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(endpoint("/settings/shifts"), {
          headers: authHeader,
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const j = await res.json();
        const normalized: ShiftPayload = Array.isArray(j)
          ? { shifts: j }
          : { shifts: j?.shifts ?? [] };
        if (!alive) return;
        setOrig(normalized);
        setValue(normalized);
      } catch (e: any) {
        if (!alive) return;
        setSnack({
          open: true,
          msg: `Failed to load shift settings: ${e?.message ?? e}`,
          severity: "error",
        });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // include api/token to re-fetch on change
  }, [api, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const changed = useMemo(
    () => JSON.stringify(value) !== JSON.stringify(orig),
    [value, orig]
  );

  const addShift = () =>
    setValue((v) => ({ shifts: [...v.shifts, emptyShift()] }));
  const removeShift = (idx: number) =>
    setValue((v) => ({ shifts: v.shifts.filter((_, i) => i !== idx) }));
  const updateShift = (idx: number, patch: Partial<Shift>) =>
    setValue((v) => ({
      shifts: v.shifts.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));

  // ---- Client-side validation for UX ----
  // Client-side validation (UX)
  // --- validation: only hard errors block Save; overlaps are warnings ---
  const { hardErrors, overlapWarnings } = useMemo(() => {
    const errs: string[] = [];
    const warns = new Set<string>();

    const toMin = (t: string) => {
      const [h, m] = t.split(":").map(Number);
      return (h || 0) * 60 + (m || 0);
    };

    const segsFor = (s: Shift) => {
      const sMin = toMin(s.start);
      const eMin = toMin(s.end);
      const segs: { day: number; start: number; end: number }[] = [];
      if (Number.isNaN(sMin) || Number.isNaN(eMin)) return segs;
      if (sMin === eMin) return segs; // handled as hard error below
      if (sMin < eMin) {
        for (const d of s.days) segs.push({ day: d, start: sMin, end: eMin });
      } else {
        // overnight split
        for (const d of s.days) segs.push({ day: d, start: sMin, end: 1440 });
        for (const d of s.days)
          segs.push({ day: (d + 1) % 7, start: 0, end: eMin });
      }
      return segs;
    };

    // per-shift hard checks
    value.shifts.forEach((s, i) => {
      const label = `Shift #${i + 1}`;
      if (!s.name.trim()) errs.push(`${label}: name is required.`);
      const sMin = toMin(s.start);
      const eMin = toMin(s.end);
      if (Number.isNaN(sMin) || Number.isNaN(eMin)) {
        errs.push(`${label}: invalid time format.`);
      } else if (sMin === eMin) {
        errs.push(
          `${label}: start and end can't be the same (24h not supported).`
        );
      }
      if (!s.days.length) errs.push(`${label}: select at least one day.`);
    });

    // non-blocking overlap detection: 1 warning per pair (deduped)
    const allSegs = value.shifts.map(segsFor);
    for (let i = 0; i < allSegs.length; i++) {
      for (let j = i + 1; j < allSegs.length; j++) {
        const A = allSegs[i],
          B = allSegs[j];
        outer: for (const a of A) {
          for (const b of B) {
            if (a.day !== b.day) continue;
            if (a.start < b.end && b.start < a.end) {
              warns.add(
                `Shifts #${i + 1} and #${j + 1} overlap on at least one day.`
              );
              break outer; // only one warning per pair
            }
          }
        }
      }
    }

    return { hardErrors: errs, overlapWarnings: Array.from(warns) };
  }, [value.shifts]);

  // ---- SAVE (diff apply -> POST /shifts, PATCH /shifts/{id}, DELETE /shifts/{id}) ----
  const save = async () => {
    if (hardErrors.length) {
      setSnack({
        open: true,
        msg: `Please fix errors before saving:\n• ${hardErrors.join("\n• ")}`,
        severity: "error",
      });
      return;
    }

    try {
      setLoading(true);

      // map existing ids
      const origById = new Map<number, Shift>();
      for (const s of orig.shifts)
        if (typeof s.id === "number") origById.set(s.id, s as Shift);

      const nextById = new Map<number, Shift>();
      const creates: Shift[] = [];
      const updates: Shift[] = [];

      for (const s of value.shifts) {
        if (typeof s.id === "number") {
          nextById.set(s.id, s);
          // compare fields to avoid no-op PATCHs
          const prev = origById.get(s.id);
          if (
            !prev ||
            prev.name !== s.name ||
            prev.start !== s.start ||
            prev.end !== s.end ||
            JSON.stringify(prev.days.slice().sort()) !==
              JSON.stringify(s.days.slice().sort())
          ) {
            updates.push(s);
          }
        } else {
          creates.push(s);
        }
      }

      // anything in orig but not in next => delete

      const deletes: number[] = [];
      origById.forEach((_val, id) => {
        if (!nextById.has(id)) deletes.push(id);
      });

      // fire requests sequentially for clarity (can batch/parallelize later)
      // DELETEs
      for (const id of deletes) {
        const res = await fetch(endpoint(`/settings/shifts/${id}`), {
          method: "DELETE",
          headers: { ...(authHeader || {}) },
        });
        if (!res.ok)
          throw new Error(
            `DELETE /shifts/${id} failed: ${res.status} ${res.statusText}`
          );
      }

      // POSTs
      for (const s of creates) {
        const res = await fetch(endpoint("/settings/shifts"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(authHeader || {}),
          },
          body: JSON.stringify({
            name: s.name,
            start: s.start,
            end: s.end,
            days: s.days,
          }),
        });
        if (!res.ok)
          throw new Error(
            `POST /shifts failed: ${res.status} ${res.statusText}`
          );
      }

      // PATCHes
      for (const s of updates) {
        const res = await fetch(endpoint(`/settings/shifts/${s.id}`), {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(authHeader || {}),
          },
          body: JSON.stringify({
            name: s.name,
            start: s.start,
            end: s.end,
            days: s.days,
          }),
        });
        if (!res.ok)
          throw new Error(
            `PATCH /shifts/${s.id} failed: ${res.status} ${res.statusText}`
          );
      }

      // re-fetch latest to refresh ids/ordering from server
      const res = await fetch(endpoint("/settings/shifts"), {
        headers: authHeader,
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const latest = await res.json();
      const normalized: ShiftPayload = Array.isArray(latest)
        ? { shifts: latest }
        : { shifts: latest?.shifts ?? [] };

      setOrig(normalized);
      setValue(normalized);
      setSnack({
        open: true,
        msg: "Shift settings saved",
        severity: "success",
      });
    } catch (e: any) {
      setSnack({
        open: true,
        msg: `Save failed: ${e?.message ?? e}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const reset = () => setValue(orig);

  return (
    <Section
      title="Shift Settings"
      subtitle="Define named shifts with start/end times and days of the week."
    >
      {loading ? (
        <Box py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
            gap={1}
            mb={1}
          >
            <Typography variant="body2" color="text.secondary">
              {value.shifts.length} shift{value.shifts.length === 1 ? "" : "s"}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Tooltip title="Add shift">
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  onClick={addShift}
                >
                  Add Shift
                </Button>
              </Tooltip>
              <Tooltip title="Reset unsaved changes">
                <span>
                  <Button
                    startIcon={<RestoreIcon />}
                    variant="text"
                    disabled={!changed}
                    onClick={reset}
                  >
                    Reset
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                onClick={save}
                disabled={!changed || hardErrors.length > 0}
              >
                Save
              </Button>
            </Box>
          </Box>

          {overlapWarnings.length > 0 && (
            <Box mb={2}>
              <Alert severity="warning" variant="outlined">
                <Typography variant="subtitle2" gutterBottom>
                  Please address the following:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {overlapWarnings.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
                <Typography variant="caption" color="text.secondary">
                  Warning only — you can still save if this is intentional.
                </Typography>
              </Alert>
            </Box>
          )}
          {hardErrors.length > 0 && (
            <Box mb={2}>
              <Alert severity="error" variant="outlined">
                <Typography variant="subtitle2" gutterBottom>
                  Please fix the following to continue:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {hardErrors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </Alert>
            </Box>
          )}

          <Box display="grid" gap={2}>
            {value.shifts.map((s, idx) => (
              <Paper
                key={s.id ?? `new-${idx}`}
                variant="outlined"
                sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                >
                  <Typography variant="subtitle1">Shift #{idx + 1}</Typography>
                  <Tooltip title="Delete shift">
                    <IconButton
                      onClick={() => removeShift(idx)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Name"
                      value={s.name}
                      onChange={(e) =>
                        updateShift(idx, { name: e.target.value })
                      }
                      fullWidth
                      placeholder="e.g., Day Shift"
                    />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <TextField
                      label="Start Time"
                      type="time"
                      value={s.start}
                      onChange={(e) =>
                        updateShift(idx, { start: e.target.value })
                      }
                      fullWidth
                      inputProps={{ step: 300 }}
                    />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <TextField
                      label="End Time"
                      type="time"
                      value={s.end}
                      onChange={(e) =>
                        updateShift(idx, { end: e.target.value })
                      }
                      fullWidth
                      inputProps={{ step: 300 }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Days of Week
                    </Typography>
                    <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                      <ToggleButtonGroup
                        value={s.days}
                        onChange={(_, newDays: number[]) =>
                          updateShift(idx, { days: newDays })
                        }
                        aria-label="days of week"
                        sx={{
                          flexWrap: "wrap",
                          "& .MuiToggleButton-root": {
                            border: (t) => `1px solid ${t.palette.divider}`,
                            borderRadius: "8px !important",
                            mx: 0.25,
                          },
                        }}
                      >
                        {DOW.map((d) => (
                          <ToggleButton
                            key={d.idx}
                            value={d.idx}
                            aria-label={d.label}
                            size="small"
                          >
                            {d.label}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Box>
                    {s.days.length > 0 && (
                      <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                        {s.days
                          .slice()
                          .sort((a, b) => a - b)
                          .map((d) => (
                            <Chip
                              key={d}
                              label={DOW.find((x) => x.idx === d)?.label ?? d}
                              size="small"
                            />
                          ))}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        </>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ whiteSpace: "pre-line", width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Section>
  );
};

export default ShiftSettingsSection;
